import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('[SignIn] Starting sign in process...');
    const { error: signInError } = await signIn(formData.email, formData.password);

    if (signInError) {
      console.error('[SignIn] Sign in error:', signInError);
      setError(signInError.message);
      setLoading(false);
    } else {
      console.log('[SignIn] Sign in successful!');
      navigate('/', { replace: true });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate flex min-h-screen w-full items-center justify-center bg-white px-6 py-16">
        {/* Blueprint grid */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
          className="relative z-10 w-full max-w-sm"
        >
          {/* Header */}
          <div className="mb-8 text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
              Steelman
            </span>
            <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
              Welcome back.
            </h1>
            <p className="mt-2 text-sm text-ink-muted">
              Sign in to continue to your debates.
            </p>
          </div>

          {/* Form card */}
          <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 shadow-frame backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25, ease: EASE }}
                    className="overflow-hidden rounded-lg border border-black/[0.08] bg-charcoal/[0.04] px-4 py-3 text-sm text-charcoal"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email */}
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-black/[0.08] bg-white px-4 py-3 text-sm text-charcoal placeholder-ink-muted/60 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10"
                />
              </div>

              {/* Password */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-ink-muted transition-colors hover:text-charcoal"
                  >
                    Forgot password?
                  </Link>
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className="w-full rounded-lg border border-black/[0.08] bg-white px-4 py-3 text-sm text-charcoal placeholder-ink-muted/60 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary mt-1 w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>

          {/* Sign up link */}
          <p className="mt-6 text-center text-sm text-ink-muted">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="font-semibold text-charcoal transition-colors hover:text-ink-muted"
            >
              Sign up
            </Link>
          </p>
        </motion.div>
      </div>
    </MotionConfig>
  );
};

export default SignIn;
